import { useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { ComputerConfig, OrderFilter, OrderVps } from "@app/types";
import { MAX_LEN, VPS_OS, VPS_Status } from "@app/constants";
import { formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import Pagination from "@app/components/pagination";
import {
  IcClock,
  IcEye,
  IcEyeOff,
  IcKeyRound,
  IcLink,
  IcMonitor,
  IcRefresh,
  IcSearch,
  IcServer,
} from "@app/components/icons";
import { CopyButton, LoadingBlock, StatusPill, btn, btnGhost, btnPrimary, btnSoft, miniBtn } from "@app/components/ui";

/** Ngày kết thúc giống cột End Time của trang Quản lý đơn hàng VPS cũ */
export const getOrderVpsEnd = (order: OrderVps) =>
  order.status === VPS_Status.END ? dayjs(order.end_time) : dayjs(order.time_start).add(order.rental_period, "month");

/** IP Remote / SSH giống trang cũ: Ubuntu dùng vps.ssh, Windows dùng vps.ip_remote */
export const remoteOf = (order: OrderVps) => (order.vps?.os === VPS_OS.UBUNTU ? order.vps?.ssh : order.vps?.ip_remote) || "";

/** Admin chưa cấp VPS: backend để TK là "đang chờ" (hoặc để trống) */
export const isWaiting = (order: OrderVps) =>
  order.status === VPS_Status.ON_RENTAL &&
  (!order.vps?.user || order.vps.user.normalize("NFC").trim().toLowerCase() === "đang chờ".normalize("NFC"));

const osName = (os?: VPS_OS) => (os === VPS_OS.UBUNTU ? "Ubuntu" : "Windows");

/** Bộ lọc VPS của tôi như bản thiết kế: Tất cả / Đang chờ / Đang thuê / Kết thúc */
export type VpsFilter = VPS_Status | "" | "waiting";

const FILTERS: [VpsFilter, string][] = [
  ["", "Tất cả"],
  ["waiting", "Đang chờ"],
  [VPS_Status.ON_RENTAL, "Đang thuê"],
  [VPS_Status.END, "Kết thúc"],
];

/**
 * Tham số status + filter gửi lên GET /orders-vps cho từng bộ lọc.
 * "Đang chờ" = đơn đang thuê mà admin chưa cấp VPS (waiting=1); "Đang thuê" bỏ các đơn đó (waiting=0).
 */
export const filterQuery = (status: VpsFilter, keyword: string): [VPS_Status | undefined, OrderFilter] =>
  status === "waiting"
    ? [undefined, { keyword, waiting: 1 }]
    : status === VPS_Status.ON_RENTAL
    ? [status, { keyword, waiting: 0 }]
    : [status || undefined, { keyword }];

interface VpsOrdersProps {
  /** Chưa có dữ liệu lần đầu */
  loading?: boolean;
  orders: OrderVps[];
  count: number;
  page: number;
  totalPages: number;
  status: VpsFilter;
  search: string;
  onSearch: (v: string) => void;
  onStatus: (v: VpsFilter) => void;
  onPageChange: (page: number) => void;
  onRdp: (order: OrderVps) => void;
  onTunnel: (order: OrderVps) => void;
  onExtend: (order: OrderVps) => void;
  onRecreate: (order: OrderVps) => void;
  /** Giờ hiện tại (ms): VPS quá ngày kết thúc thì hiện "Kết thúc" ngay, không đợi server */
  now?: number;
}

/** "VPS của tôi": thay cho bảng ở trang Quản lý đơn hàng VPS cũ. */
function VpsOrders({
  orders,
  count,
  page,
  totalPages,
  status,
  search,
  onSearch,
  onStatus,
  onPageChange,
  loading,
  now,
  ...on
}: VpsOrdersProps) {
  const t = useT();
  const [revealed, setRevealed] = useState<string[]>([]);

  const toggleReveal = (id: string) =>
    setRevealed((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const label = "w-[22px] flex-none font-sans text-[11px] font-semibold text-faint";

  return (
    <section id="vps-orders" className="scroll-mt-20 overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="border-b border-line px-5 py-4 max-[760px]:px-3.5">
        <h2 className="flex items-center gap-2 text-[17px] font-bold">
          {t("VPS của tôi")}
          <span className="rounded-full bg-brand-soft px-[9px] py-px text-xs font-semibold text-brand">{count}</span>
        </h2>
        <p className="m-0 mt-0.5 text-[13px] text-muted">
          {t("Theo dõi trạng thái, thông tin đăng nhập và gia hạn từng VPS.")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-b border-line px-5 py-3 max-[760px]:px-3.5">
        <label className="flex h-9 flex-[1_1_240px] items-center gap-2 rounded-[10px] border border-line bg-surface px-3 text-muted focus-within:border-brand">
          <IcSearch className="h-[18px] w-[18px] flex-none" />
          <input
            type="search"
            maxLength={MAX_LEN.SEARCH}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t("Tìm theo cấu hình, mã VPS, IP…")}
            className="min-w-0 flex-1 border-0 bg-transparent text-fg outline-none"
          />
        </label>
        <div className="rtabs inline-flex flex-wrap gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
          {FILTERS.map(([key, text]) => (
            <button
              key={key || "all"}
              type="button"
              onClick={() => onStatus(key)}
              className={clsx(
                "inline-flex items-baseline gap-1.5 rounded-[7px] px-3 py-[5px] text-[13px] font-semibold",
                status === key ? "bg-surface text-fg shadow-card" : "text-muted"
              )}
            >
              {t(text)}
              {status === key && <em className="text-[11.5px] not-italic text-faint">{count}</em>}
            </button>
          ))}
        </div>
      </div>

      {loading && !orders.length ? (
        <LoadingBlock />
      ) : !orders.length ? (
        <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
          <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">
            <IcServer className="h-[26px] w-[26px]" />
          </div>
          <h3 className="text-base font-bold">
            {search || status ? t("Không tìm thấy VPS nào") : t("Bạn chưa thuê VPS nào")}
          </h3>
          <p className="m-0 text-muted">
            {search || status
              ? t("Thử từ khoá khác hoặc chọn bộ lọc \"Tất cả\".")
              : t("Chọn một gói ở trên, VPS sẽ hiện tại đây ngay sau khi đặt.")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(330px,100%),1fr))] gap-3 p-5 max-[760px]:p-3.5">
          {orders.map((order) => {
            const config = order.vps?.config as ComputerConfig | undefined;
            const end = getOrderVpsEnd(order);
            // quá ngày kết thúc mà cron server chưa đổi status cũng coi là đã kết thúc
            const timeUp = order.status === VPS_Status.ON_RENTAL && !!now && end.valueOf() <= now;
            const ended = order.status === VPS_Status.END || timeUp;
            const waiting = !timeUp && isWaiting(order);
            const running = order.status === VPS_Status.ON_RENTAL && !waiting && !timeUp;
            const days = Math.ceil(end.diff(dayjs(), "hour") / 24);
            const soon = running && days <= 3;
            const remote = remoteOf(order);
            const shown = revealed.includes(order._id);
            const vpsId = `VPS_${order.vps?._id}`;

            return (
              <article
                key={order._id}
                className={clsx(
                  "flex min-w-0 flex-col gap-2.5 rounded-card border bg-surface p-3.5 transition-shadow hover:shadow-card",
                  running ? "border-brand shadow-[0_0_0_3px_var(--brand-soft)]" : "border-line"
                )}
              >
                {/* gói, OS, ID, trạng thái */}
                <div className="flex items-start gap-2.5">
                  <span
                    className={clsx(
                      "grid h-[38px] w-[38px] flex-none place-items-center rounded-[9px] bg-brand-soft text-brand",
                      ended && "opacity-60 grayscale"
                    )}
                  >
                    <IcServer className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* có tên khách đặt thì hiện tên, dòng dưới là gói (như bản thiết kế) */}
                    <b className="block truncate font-bold">{order.name || config?.config_name}</b>
                    <small className="text-xs text-muted">
                      {order.name && config?.config_name ? `${config.config_name} · ` : ""}
                      {osName(order.vps?.os)} · {order.rental_period} {t("tháng")}
                    </small>
                    <div className="flex items-center gap-0.5 text-[11.5px] text-muted">
                      {t("ID VPS")}:{" "}
                      <span className="font-mono" title={vpsId}>
                        {vpsId.slice(0, 10)}…
                      </span>
                      <CopyButton value={vpsId} label="Sao chép ID VPS" />
                    </div>
                  </div>
                  {running && <StatusPill tone="ok">{t("Đang thuê")}</StatusPill>}
                  {waiting && <StatusPill tone="warn">{t("Đang chờ")}</StatusPill>}
                  {ended && <StatusPill tone="muted">{t("Kết thúc")}</StatusPill>}
                  {order.status === VPS_Status.ALREADY && <StatusPill tone="warn">{t("Sẵn sàng")}</StatusPill>}
                </div>

                {/* bắt đầu, kết thúc */}
                <div className="grid grid-cols-2 gap-1.5 text-[12.5px] tabular-nums">
                  <div className="min-w-0 rounded-lg bg-surface-2 px-2.5 py-1.5">
                    <span className="block text-[11px] text-muted">{t("Bắt đầu")}</span>
                    <b>{dayjs(order.time_start).format("DD/MM/YYYY HH:mm")}</b>
                  </div>
                  <div className="min-w-0 rounded-lg bg-surface-2 px-2.5 py-1.5">
                    <span className="block text-[11px] text-muted">
                      {t("Kết thúc")}
                      {!ended && ` · ${t("còn")} ${Math.max(0, days)} ${t("ngày")}`}
                    </span>
                    <b className={clsx(soon && "text-warn")}>{end.format("DD/MM/YYYY HH:mm")}</b>
                  </div>
                </div>

                <p className="m-0 text-[12.5px] tabular-nums text-muted">
                  <b className="text-fg">{formatMoneyVN(config?.monthly_rental_price || 0)}đ</b>/{t("tháng")} ·{" "}
                  {order.rental_period} {t("tháng")}
                  {!!order.discount && (
                    <>
                      {" "}
                      · {t("chiết khấu")} <b className="text-fg">{formatMoneyVN(order.discount)}đ</b>
                    </>
                  )}
                </p>

                {/* đăng nhập */}
                <div className="rounded-[9px] bg-surface-2 px-2.5 py-2">
                  {ended || waiting ? (
                    <div className="flex items-center gap-2 text-xs leading-snug text-faint">
                      <IcKeyRound className="h-[15px] w-[15px] flex-none" />
                      {ended
                        ? t("VPS đã kết thúc, thông tin đăng nhập đã thu hồi")
                        : t("Đang chờ hệ thống cấp tài khoản, mật khẩu và IP")}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-px font-mono text-[12.5px]">
                      <div className="flex items-center gap-1">
                        <span className={label}>TK</span>
                        <span className="min-w-0 truncate">{order.vps.user}</span>
                        <CopyButton value={order.vps.user} label="Sao chép tài khoản" />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={label}>MK</span>
                        <span className="min-w-0 truncate">{shown ? order.vps.pass : "••••••••"}</span>
                        <button
                          type="button"
                          className={miniBtn}
                          title={shown ? t("Ẩn mật khẩu") : t("Hiện mật khẩu")}
                          onClick={() => toggleReveal(order._id)}
                        >
                          {shown ? <IcEyeOff className="h-[15px] w-[15px]" /> : <IcEye className="h-[15px] w-[15px]" />}
                        </button>
                        <CopyButton value={order.vps.pass} label="Sao chép mật khẩu" />
                      </div>
                      {remote && (
                        <div className="flex items-center gap-1">
                          <span className={label}>IP</span>
                          <span className="min-w-0 truncate" title={remote}>
                            {remote}
                          </span>
                          <CopyButton value={remote} label="Sao chép địa chỉ remote" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {waiting && (
                  <div className="flex items-start gap-2 rounded-[9px] bg-warn-soft px-2.5 py-2 text-[12.5px] font-medium leading-snug text-warn">
                    <IcClock className="mt-px h-4 w-4 flex-none" />
                    {t("Hệ thống đang cấp VPS. Khi xong, trạng thái chuyển sang Đang thuê và thông tin đăng nhập hiện ở đây.")}
                  </div>
                )}

                {soon && (
                  <div className="flex items-center gap-2 rounded-[9px] bg-warn-soft px-2.5 py-2 text-[12.5px] font-medium text-warn">
                    <IcClock className="h-4 w-4 flex-none" />
                    {t("VPS sẽ hết hạn sau")} {Math.max(0, days)} {t("ngày. Gia hạn để không bị gián đoạn.")}
                  </div>
                )}

                {/* thao tác: chỉ đơn Đang thuê mới có Action như trang cũ */}
                <div className="mt-auto flex flex-wrap gap-1.5">
                  {waiting && (
                    <>
                      <button
                        type="button"
                        disabled
                        title={t("Dùng được khi VPS đã sẵn sàng")}
                        className={clsx(btn, btnPrimary, "flex-1 disabled:cursor-not-allowed disabled:opacity-50")}
                      >
                        <IcMonitor className="h-4 w-4" />
                        Remote Desktop
                      </button>
                      <button
                        type="button"
                        disabled
                        title={t("Dùng được khi VPS đã sẵn sàng")}
                        className={clsx(btn, btnGhost, "flex-1 disabled:cursor-not-allowed disabled:opacity-50")}
                      >
                        <IcLink className="h-4 w-4" />
                        PCRender Tunnel
                      </button>
                      <button type="button" className={clsx(btn, btnGhost, "w-full")} onClick={() => on.onExtend(order)}>
                        <IcClock className="h-4 w-4" />
                        {t("Gia hạn")}
                      </button>
                    </>
                  )}
                  {running && (
                    <>
                      {/* Remote Desktop cho mọi VPS đang thuê (tải file .rdp như Cloud Gaming):
                          API dùng ip_remote, VPS Ubuntu không có thì dùng địa chỉ ở trường ssh */}
                      <button
                        type="button"
                        disabled={!(order.vps?.ip_remote || order.vps?.ssh)}
                        title={order.vps?.ip_remote || order.vps?.ssh ? undefined : t("VPS chưa có IP Remote")}
                        className={clsx(btn, btnPrimary, "flex-1 disabled:cursor-not-allowed disabled:opacity-50")}
                        onClick={() => on.onRdp(order)}
                      >
                        <IcMonitor className="h-4 w-4" />
                        Remote Desktop
                      </button>
                      <button type="button" className={clsx(btn, btnGhost, "flex-1")} onClick={() => on.onTunnel(order)}>
                        <IcLink className="h-4 w-4" />
                        PCRender Tunnel
                      </button>
                      <button
                        type="button"
                        className={clsx(btn, soon ? btnPrimary : btnGhost, "w-full")}
                        onClick={() => on.onExtend(order)}
                      >
                        <IcClock className="h-4 w-4" />
                        {t("Gia hạn")}
                      </button>
                    </>
                  )}
                  {ended && config && (
                    <button type="button" className={clsx(btn, btnSoft, "flex-1")} onClick={() => on.onRecreate(order)}>
                      <IcRefresh className="h-4 w-4" />
                      {t("Tạo lại VPS")}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </section>
  );
}

export default VpsOrders;
